import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Icon } from '../design-system/icons';
import { ResultImageOverlay } from '../components/onboarding/ResultImageOverlay';
import { useLocation, useNavigate } from 'react-router-dom';
import { SetupPricingSheet } from '../components/pricing/SetupPricingSheet';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import {
  GENERATION_RESULTS,
  SETUP_GENERATION_KEY,
  type ResultImage,
} from '../components/onboarding/accountSetup';
import { useUserGroup } from '../hooks/useUserGroup';
import { V2_STUDIO_PATH } from '../lib/userGroups';
import { marcarDescargaDelOnboarding } from '../lib/onboardingTracking';
import { abreElCatalogoDePlanes, notaDeCreditos } from '../lib/cuestionarioSegunPlan';
import { MarcoDelOnboarding } from '../components/onboarding/marca/MarcoDelOnboarding';
import { PantallaConCumbre } from '../components/onboarding/corto/PantallaConCumbre';
import type { Gesto } from '../components/onboarding/marca/GuiaCumbre';
import { dialogoDeLosResultados } from '../lib/cuestionarioCorto/dialogo';

/**
 * Pantalla de resultados de la generación (onboarding).
 *
 * Con el diseño del onboarding de marca, igual en el teléfono y en desktop
 * (ADR-2026-10-31-el-cuestionario-corto-con-el-diseno-del-de-marca): el marco, Cumbre que
 * presenta las fotos, una grilla y, abajo, la nota de créditos y "Seguir explorando Atlas".
 * Tocar una foto abre la vista ampliada, que tiene la descarga. Antes eran dos layouts y el de
 * desktop iba dentro del shell de la app.
 *
 * Las miniaturas llevan texto debajo **sólo con los resultados de ejemplo**
 * (ADR-2026-08-04-label-solo-en-los-resultados-de-ejemplo): ahí el label dice algo
 * ("Foto catálogo", "Video"), mientras que en la generación real es "Opción N" y sólo
 * hace ruido. El `label` existe en los dos casos igual, porque además de mostrarse es la
 * etiqueta accesible (`aria-label`, `alt`) y el nombre del archivo al descargar.
 *
 * Los resultados dependen del ejemplo preseleccionado en el paso 4 (`localStorage`):
 * "Cartera verde" o "Camisa de hombre" (fallback `cartera_verde`).
 */
/**
 * Botón de play centrado sobre el póster de un resultado de tipo "Video".
 * Replica el badge de Figma (node 195:195): círculo `rgba(0,0,0,0.8)` con
 * padding de 12px y el ícono `solid/play` del DS (20px, blanco). El `size` es
 * el **diámetro** del círculo; el ícono escala proporcional (20/44 en el diseño).
 */
function PlayBadge({ size = 44 }: { size?: number }) {
  const iconSize = Math.round(size * (20 / 44));
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span
        className="flex items-center justify-center rounded-full bg-black/80"
        style={{ width: size, height: size }}
      >
        <Icon name="solid/play" size={iconSize} className="text-white" />
      </span>
    </span>
  );
}

export interface AccountSetupResultsProps {
  /**
   * Preview de desarrollo (ADR-2026-07-31-pantalla-resultados-alta-upsell-hacer). Sólo lo pasa la ruta `/onboarding-pricing-dev`,
   * que está detrás de `import.meta.env.DEV`, así que en producción llega siempre
   * `false` y la pantalla se comporta exactamente como antes.
   *
   * Hace dos cosas, las dos para poder mirar el upsell de planes sin reproducir el
   * alta entera:
   * - **No rebota a `/bienvenida` sin sesión.** Se puede porque esta pantalla no
   *   pide datos: los resultados son estáticos (`GENERATION_RESULTS`). Es la razón
   *   por la que ADR-2026-07-30-bypass-dev-onboarding-entrar-app no tocó este guard en las otras pantallas — allá sí hacen
   *   falta datos de Supabase.
   * - **"Seguir explorando Atlas" abre el catálogo de planes**, que fuera de la preview ya no se
   *   abre (ADR-2026-10-31-descargar-no-abre-los-planes).
   */
  devPreview?: boolean;
}

export const AccountSetupResults = ({ devPreview = false }: AccountSetupResultsProps = {}) => {
  const navigate = useNavigate();

  // La salida del onboarding es el estudio (`V2_STUDIO_PATH`). Antes salía por grupo y un
  // usuario recién creado, con el grupo todavía en null (lo asigna el worker post-setup,
  // async), podía caer en el flujo clásico — que se borró en
  // ADR-2026-10-28-se-borra-el-flujo-clasico. El prompt
  // "Instalar como app / Abrir en navegador" está montado y auto-gateado en el estudio:
  // aparece solo a los ~2s de aterrizar.
  // `plan` sale de `useUserGroup`, que ya lee la fila de la empresa: saber si esta cuenta
  // ya paga no cuesta una consulta más en una pantalla que además está precargando
  // imágenes. Ver ADR-2026-09-22-el-cuestionario-no-le-vende-a-quien-ya-pago.
  const { plan } = useUserGroup();
  // `state.resultadosReales`: las imágenes generadas de verdad que manda la pantalla
  // de generación cuando el usuario subió su propia foto.
  const location = useLocation();
  const [results, setResults] = useState<ResultImage[]>(GENERATION_RESULTS.cartera_verde);
  /**
   * ¿Estos resultados salieron de una generación REAL (el usuario subió su foto) o son
   * el set de demo? Decide si las miniaturas llevan texto debajo
   * (ADR-2026-08-04-label-solo-en-los-resultados-de-ejemplo): los de ejemplo tienen un
   * nombre que dice algo ("Foto catálogo", "Video") y los reales sólo "Opción N".
   *
   * Se resuelve por ORIGEN y no mirando el label, porque el label sigue existiendo en
   * los dos casos — se usa igual para el `alt`, el `aria-label` y el nombre del archivo
   * al descargar.
   */
  const [sonGeneradas, setSonGeneradas] = useState(false);
  const [preview, setPreview] = useState<ResultImage | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const tramos = useMemo(() => dialogoDeLosResultados(sonGeneradas), [sonGeneradas]);
  // Cumbre festeja una vez, al llegar.
  const [gesto] = useState<Gesto>({ tipo: 'festeja', n: 1 });

  useEffect(() => {
    let active = true;
    if (!devPreview) {
      supabase.auth.getSession().then(({ data }) => {
        if (active && !data.session)
          navigate(ONBOARDING_V2.bienvenida, { replace: true });
      });
    }
    let id: string | null = null;
    try {
      id = localStorage.getItem(SETUP_GENERATION_KEY);
    } catch {}
    // Generación REAL (el usuario subió su foto): las imágenes vienen ya precargadas
    // desde la pantalla de generación, con sus URLs firmadas
    // (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real). Tienen prioridad sobre el set
    // de demo; si no hay (ejemplo precargado, o refresh que perdió el state) se usa el
    // set del ejemplo elegido.
    const reales = (location.state as { resultadosReales?: ResultImage[] } | null)
      ?.resultadosReales;
    if (reales?.length) {
      setResults(reales);
      setSonGeneradas(true);
    } else if (id && GENERATION_RESULTS[id]) {
      setResults(GENERATION_RESULTS[id]);
      setSonGeneradas(false);
    }
    return () => {
      active = false;
    };
  }, [navigate, devPreview, location.state]);

  // Precarga por detrás las versiones ORIGINALES full-res (`full`) para que la vista
  // ampliada las muestre al instante.
  useEffect(() => {
    const preloaders = results
      .filter((img) => img.full && img.full !== img.src)
      .map((img) => {
        const pre = new Image();
        pre.src = img.full!;
        return pre;
      });
    return () => {
      preloaders.forEach((pre) => {
        pre.src = '';
      });
    };
  }, [results]);

  /**
   * Marca que el usuario descargó (cualquier resultado, desde la vista ampliada), en la
   * empresa en Supabase, para poder medirlo desde afuera
   * (ADR-2026-08-07-onboarding-producto-y-descarga-en-supabase). Ya no abre los planes al salir
   * (ADR-2026-10-31-descargar-no-abre-los-planes).
   *
   * El update a Supabase sale **una vez por visita a la pantalla**: bajarse las cuatro
   * imágenes no son cuatro updates, y a la vez un refresh vuelve a intentarlo si la
   * primera vez no llegó.
   */
  const marcadoEnSupabase = useRef(false);
  const markDownloaded = () => {
    if (!marcadoEnSupabase.current) {
      marcadoEnSupabase.current = true;
      marcarDescargaDelOnboarding();
    }
  };

  /**
   * "Seguir explorando Atlas": al estudio. Descargar una imagen ya no abre antes el catálogo de
   * planes (ADR-2026-10-31-descargar-no-abre-los-planes); sólo lo abre la preview de
   * desarrollo, y esa regla vive en `abreElCatalogoDePlanes`.
   */
  const handleContinue = () => {
    if (abreElCatalogoDePlanes({ previewDeDesarrollo: devPreview })) {
      setShowPricing(true);
      return;
    }
    // Fin del onboarding: al estudio.
    // Antes acá venía el alta clásica (`/onboarding`), que ya no existe — la
    // empresa se creó al terminar el paso 3 (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
    navigate(V2_STUDIO_PATH);
  };

  /**
   * El back del navegador (o el gesto de "volver" del celular, el edge-swipe) **no puede
   * devolver al paso de generar**: el alta ya terminó, la empresa está creada y el paso 4
   * volvería a ofrecer generar (el jefe lo reprodujo justo con el edge-swipe del celular).
   * Acá el gesto **no hace nada** — el usuario sale por el botón "Seguir explorando Atlas".
   *
   * Cómo: se apila una entrada propia al montar y, cada vez que el back la consume, se
   * vuelve a apilar → el gesto queda neutralizado SOLO en esta pantalla (el efecto se
   * limpia al desmontar; el resto de la web conserva el gesto). Combinado con llegar acá
   * con `replace` (el paso de generar ni queda en el historial). Y si un pop MÚLTIPLE (un
   * swipe muy largo que se come varias entradas de una) llegara a sacarnos igual, se
   * detecta y se vuelve a resultados por router antes de re-armar → nunca se queda trabado
   * en el cuestionario (desde donde se podría regenerar). Re-apilar `window.location.href`
   * suelto no alcanzaba: tras un pop múltiple es la URL ya popeada, no la de resultados.
   * Greptile PR#70.
   *
   * En la preview de dev NO se traba: es una pantalla para mirar y trabar el back molesta.
   * Supersede ADR-2026-08-03-back-en-resultados-sale-como-el-boton (que hacía back = botón,
   * pero no aguantaba el edge-swipe de iOS). Ver ADR-2026-08-14-onboarding-back-no-regenera.
   */
  useEffect(() => {
    if (devPreview) return;
    const onPop = () => {
      // Si un pop múltiple nos sacó de resultados, volvemos por router (re-render correcto)
      // ANTES de re-armar; si seguimos acá (back normal), sólo re-armamos → no hace nada.
      if (window.location.pathname !== ONBOARDING_V2.setupResultados) {
        navigate(ONBOARDING_V2.setupResultados, { replace: true });
      }
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [devPreview, navigate]);

  /**
   * Salida del sheet (cerrar / arrastrar / omitir / suscribirse): continúa el
   * flujo. Llega ya con la animación de salida terminada, así que desmontarlo
   * acá no corta nada.
   */
  const handlePricingDismiss = () => {
    setShowPricing(false);
    navigate(V2_STUDIO_PATH);
  };

  return (
    <MarcoDelOnboarding
      pasos={null}
      despues={
        <>
          {preview && <ResultImageOverlay image={preview} onClose={() => setPreview(null)} onDownload={markDownloaded} />}
          {showPricing && <SetupPricingSheet onDismiss={handlePricingDismiss} />}
        </>
      }
    >
      <main className="om-pantalla">
        <PantallaConCumbre
          tramos={tramos}
          gesto={gesto}
          barra={
            <>
              {/* En el teléfono, el botón primero y la nota debajo; en desktop, la nota a su izquierda. */}
              <button type="button" onClick={handleContinue} className="om-boton md:order-last md:w-auto md:px-10">
                Seguir explorando Atlas
              </button>
              <p className="om-barra__aviso flex items-center justify-center gap-1.5 md:justify-end">
                <Icon name="solid/sparkles" size={14} className="shrink-0 text-brand-primary" />
                {notaDeCreditos(plan)}
              </p>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {results.map((img) => (
              <button
                key={img.label}
                type="button"
                onClick={() => setPreview(img)}
                aria-label={`Ver ${img.label} en pantalla completa`}
                className="group flex flex-col items-center gap-2 rounded-[18px] outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                <span className="relative block w-full overflow-hidden rounded-[18px] border-[1.5px] border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-white shadow-[0_12px_24px_-20px_rgb(var(--brand-primary-rgb)_/_0.55)] transition-[transform,box-shadow] duration-200 ease-out group-hover:-translate-y-0.5 group-hover:shadow-[0_16px_28px_-18px_rgb(var(--brand-primary-rgb)_/_0.65)] group-active:scale-[0.98]">
                  <img src={img.src} alt={img.label} draggable={false} className="aspect-[4/5] w-full object-cover" />
                  {img.isVideo && <PlayBadge />}
                </span>
                {!sonGeneradas && <span className="text-[14px] font-semibold leading-[19px] text-[#4B5275]">{img.label}</span>}
              </button>
            ))}
          </div>
        </PantallaConCumbre>
      </main>
    </MarcoDelOnboarding>
  );
};

export default AccountSetupResults;
